import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { STRINGS } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const DEFAULTS = { theme: "auto", furigana: "selective", bilingual: "stacked", ui_lang: "id", jp_scale: 1 };
const PrefsCtx = createContext(null);

function load() {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("yk_prefs") || "{}") }; } catch { return DEFAULTS; }
}

export function PrefsProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(load);

  useEffect(() => {
    if (!user) return;
    api.get("/preferences").then(({ data }) => {
      setPrefs((p) => ({ ...p, ...data }));
      localStorage.setItem("yk_prefs", JSON.stringify({ ...DEFAULTS, ...data }));
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = prefs.theme === "dark" || (prefs.theme === "auto" && mq.matches);
      document.documentElement.classList.toggle("dark", dark);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [prefs.theme]);

  useEffect(() => { document.documentElement.style.setProperty("--jp-scale", String(prefs.jp_scale || 1)); }, [prefs.jp_scale]);

  const update = async (partial) => {
    const next = { ...prefs, ...partial };
    setPrefs(next);
    localStorage.setItem("yk_prefs", JSON.stringify(next));
    if (user) api.put("/preferences", partial).catch(() => {});
  };

  const dict = STRINGS[prefs.ui_lang] || STRINGS.id;
  const t = (key) => dict[key] ?? STRINGS.id[key] ?? key;
  const area = (a) => dict.areas[a] || a;

  return <PrefsCtx.Provider value={{ prefs, update, t, area, lang: prefs.ui_lang }}>{children}</PrefsCtx.Provider>;
}

export const usePrefs = () => useContext(PrefsCtx);
